import React from 'react';
import { Header } from './components/Header';
import { Hero } from './components/Hero';
import { ContentSection } from './components/ContentSection';
import { CTAButton } from './components/CTAButton';
import { FAQ } from './components/FAQ';
import { Footer } from './components/Footer';
import { PricingCards } from './components/PricingCards';
import { GuaranteeBadge } from './components/GuaranteeBadge';
import { TestimonialsSection } from './components/TestimonialsSection';
import { ProgramDetails } from './components/ProgramDetails';
import { JolenaIntro } from './components/JolenaIntro';
import { ExpressiveRewiring } from './components/ExpressiveRewiring';
import { NervousSystemFreeze } from './components/NervousSystemFreeze';
import { LimitedTimeOffer } from './components/LimitedTimeOffer';
import { CheckIcon, LockIcon, ClockIcon } from 'lucide-react';
export function App() {
  return <div className="w-full min-h-full font-sans bg-white" data-id="element-0">
      <Header data-id="element-1" />
      <main className="w-full max-w-4xl mx-auto px-4 sm:px-6" data-id="element-2">
        <Hero data-id="element-3" />
        <ContentSection title="Most people think emotional numbness is a sign that something's broken inside them—something they need to fix or force back open." content={<>
              <p className="text-gray-700 mb-4" data-id="element-5">They're told things like:</p>
              <p className="text-gray-700 mb-4 italic" data-id="element-6">
                "Just breathe and stay present." "Talk about it until you feel
                something." "Push through the numbness. Just keep trying."
              </p>
              <p className="text-gray-700 mb-4 font-bold" data-id="element-7">
                But emotional numbness isn't a failure of effort, courage, or
                even healing.
              </p>
              <p className="text-gray-700 mb-4" data-id="element-8">
                It's a nervous system response called "freeze."
              </p>
              <p className="text-gray-700 mb-4" data-id="element-9">
                When your nervous system has carried too much stress, trauma, or
                overwhelm for too long, it doesn't always go into panic or
                anxiety.
              </p>
              <p className="text-gray-700 mb-4" data-id="element-10">
                Instead, it quietly shifts into shutdown—like a fuse switching
                off to prevent overload.
              </p>
              <p className="text-gray-700 mb-4" data-id="element-11">
                Your system chooses numbness because it feels safer to
                disconnect from feeling entirely than to keep being overwhelmed
                by emotions you don't have the capacity or support to process.
              </p>
            </>} data-id="element-4" />
        <JolenaIntro data-id="element-12" />
        <NervousSystemFreeze data-id="element-13" />
        <ExpressiveRewiring data-id="element-14" />
        <TestimonialsSection data-id="element-15" />
        <ContentSection title="Unfreeze: A 4-Week Creative Nervous System Reset for Emotional Numbness & Trauma Shutdown" titleClass="text-3xl md:text-4xl text-purple-800" containerClass="bg-gradient-to-r from-purple-50 to-teal-50 shadow-sm" content={<>
              <div className="w-full my-8" data-id="element-17">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/uUA5vfn2aoPYgRbaitC7ga/Mockups_%2814%29.png" alt="Neurotoned Membership" className="w-full max-w-2xl mx-auto h-auto object-cover rounded-lg shadow-md" data-id="element-18" />
              </div>
              <p className="text-gray-700 mb-4" data-id="element-19">
                This isn't a course. It's not therapy. And it's not another
                mindset program that tells you to "just feel it."
              </p>
              <p className="text-gray-700 mb-4" data-id="element-20">
                Unfreeze is a step-by-step, body-led protocol to gently reawaken
                the parts of you that went quiet—through sound, movement, and
                expressive ritual.
              </p>
              <p className="text-gray-700 mb-4" data-id="element-21">
                It's for the moments when breathwork feels too far away. When
                journaling makes you feel more numb, not less. When traditional
                healing tools hit a wall—and you're left wondering if something
                inside you shut off for good.
              </p>
              <p className="text-gray-700 mb-4" data-id="element-22">
                Here's what happens when it starts to work:
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-700" data-id="element-23">
                <li className="mb-2" data-id="element-24">
                  You feel a flicker of emotion in a place that's been silent
                  for years
                </li>
                <li className="mb-2" data-id="element-25">
                  You cry—not because you force it—but because your body finally
                  says, "I'm ready now"
                </li>
                <li className="mb-2" data-id="element-26">
                  You notice your voice soften, your gestures become freer, your
                  eyes brighten
                </li>
                <li className="mb-2" data-id="element-27">
                  You feel your self come back online—gently, safely, and in
                  rhythm with your own pace
                </li>
              </ul>
              <p className="text-gray-700 mb-4" data-id="element-28">
                And it's not because you "figured something out."
              </p>
              <p className="text-gray-700 mb-4" data-id="element-29">
                It's because you created enough safety, rhythm, and invitation
                for your nervous system to return to feeling.
              </p>
              <p className="text-gray-700 mb-4" data-id="element-30">
                This is the transformation. Not a breakthrough moment. Not an
                emotional collapse.
              </p>
              <p className="text-gray-700 mb-4" data-id="element-31">
                But a slow, steady defrosting of everything that once felt
                unreachable.
              </p>
              <div className="mt-6 flex justify-center" data-id="element-32">
                <CTAButton text="Start Program" href="#pricing" scarcity="Enrollment open for a limited time" data-id="element-33" />
              </div>
            </>} data-id="element-16" />
        <ProgramDetails data-id="element-34" />
        <ContentSection title="What This Program Really Offers You:" containerClass="bg-gradient-to-r from-teal-50 to-purple-50 shadow-sm" content={<>
              <p className="text-gray-700 mb-4" data-id="element-36">
                Not more tools to "fix" yourself. Not a checklist of things to
                do when you feel numb.
              </p>
              <p className="text-gray-700 mb-4" data-id="element-37">
                But a creative, body-led return to the parts of you that still
                long to feel, connect, and be fully alive again.
              </p>
              <p className="text-gray-700 mb-4" data-id="element-38">
                This is how you begin to thaw what went silent.
              </p>
              <p className="text-gray-700 mb-4" data-id="element-39">
                And you don't have to do it alone.
              </p>
              <p className="text-gray-700 mb-4" data-id="element-40">
                We know what it's like to spend hundreds (even thousands) trying
                to feel something again... Trying every method, every course,
                every expert—only to be left with more tools you can't access
                and more guilt for not "getting better."
              </p>
              <p className="text-gray-700 mb-4" data-id="element-41">
                We never want cost to be another reason you feel stuck.
              </p>
              <p className="text-gray-700 mb-4" data-id="element-42">So here's what we've done:</p>
              <div className="mt-6 flex justify-center" data-id="element-43">
                <CTAButton text="Start Program" href="#pricing" scarcity="Enrollment open for a limited time" data-id="element-44" />
              </div>
            </>} data-id="element-35" />
        <div id="pricing" className="scroll-mt-20" data-id="element-45">
          <PricingCards data-id="element-46" />
        </div>
        <div className="mt-10 mb-6 flex justify-center" data-id="element-47">
          <GuaranteeBadge data-id="element-48" />
        </div>
        <ContentSection title="If something in you quietly whispered 'yes' as you read this page..." containerClass="bg-gradient-to-r from-purple-50 to-teal-50 shadow-sm" content={<>
              <p className="text-gray-700 mb-4" data-id="element-50">
                If you're tired of feeling cut off from yourself... If you're
                ready to try something different—not more effort, not more
                pushing, but a soft return to feeling...
              </p>
              <p className="text-gray-700 mb-4" data-id="element-51">
                Then now is the time to begin.
              </p>
              <p className="text-gray-700 mb-6" data-id="element-52">
                You don't need to feel ready. You just need to begin.
              </p>
              <div className="flex justify-center" data-id="element-53">
                <CTAButton text="Start Program" href="#pricing" scarcity="Enrollment open for a limited time - Join now to secure your spot" data-id="element-54" />
              </div>
            </>} data-id="element-49" />
        <FAQ data-id="element-55" />
        <div className="mt-10 mb-16 flex justify-center" data-id="element-56">
          <CTAButton text="Start Program" href="#pricing" scarcity="Enrollment open for a limited time - Join now to secure your spot" data-id="element-57" />
        </div>
      </main>
      <Footer data-id="element-58" />
    </div>;
}